<template></template>

<script>
  import { Device } from 'twilio-client'
  import { createNamespacedHelpers } from 'vuex'
  import EventBus from '@/lib/event-bus'
  import store from '@/lib/store'
  import { jiminnyHost } from '@/helpers/region'
  import notyMixin from '@/helpers/noty'
  import * as Sentry from '@sentry/browser'
  import VError from 'verror'
  import swal from 'sweetalert2'
  import axios from 'axios'
  import TwilioClientLogger from './twilio-client-logger'
  import * as Notifications from '../notifications'

  const { mapState, mapActions } = createNamespacedHelpers('conference')

  let highRttNotification
  let lowMosNotification
  let highJitterNotification
  let highPacketLossNotification
  let constantAudioInputLevelNotification
  let constantAudioOutputLevelNotification

  export default {
    mixins: [
      notyMixin
    ],
    props: [
      'isGhost'
    ],
    data () {
      return {
        user: store.get('user'),
        team: store.get('team'),
        deviceId: store.get('deviceId'),
        deviceInitialized: false,
        connectOnDeviceReady: false,
        endedByUser: false
      }
    },
    computed: mapState([
      'activity'
    ]),
    created () {
      this.$_device = new Device() // eslint-disable-line camelcase
      this.$_logger = new TwilioClientLogger() // eslint-disable-line camelcase

      EventBus.$on('JOIN_CONFERENCE_CALL', this.joinConferenceCall)
      EventBus.$on('LEAVE_CONFERENCE_CALL', this.leaveConferenceCall)
      EventBus.$on('TOGGLE_MUTE', this.toggleMute)
      EventBus.$on('DIALPAD_KEY', this.handleDialpadKey)
    },
    beforeDestroy () {
      this.$_device.disconnectAll()
      this.$_device.destroy()

      EventBus.$off('JOIN_CONFERENCE_CALL', this.joinConferenceCall)
      EventBus.$off('LEAVE_CONFERENCE_CALL', this.leaveConferenceCall)
      EventBus.$off('TOGGLE_MUTE', this.toggleMute)
      EventBus.$off('DIALPAD_KEY', this.handleDialpadKey)
    },
    methods: {
      ...mapActions([
        'setIsMuted',
        'exitCallScreen'
      ]),
      async joinConferenceCall () {
        try {
          if (this.deviceInitialized) {
            if (this.$_device.status() === 'ready') { // Cannot call .status() if device hasn't been setup yet
              this.connect()
            } else if (this.$_device.status() === 'offline') {
              this.connectOnDeviceReady = true // The device "ready" event handler will make the call when device is ready
            } else if (this.$_device.status() === 'busy') {
              // Nothing to do as joinConferenceCall method cannot be invoked when there is an active call
            }
          } else {
            this.connectOnDeviceReady = true // The device "ready" event handler will make the call when device is ready
            await this.initializeDevice()
            this.deviceInitialized = true
          }
        } catch (error) {
          this.$_logger.error(error)

          Sentry.withScope(scope => {
            scope.setExtra('debug', false)
            Sentry.captureMessage(VError.fullStack(error))
          })

          this.$router.push({
            path: '/error-page',
            query: {
              error: encodeURIComponent('Sorry, an error occurred. Please contact customer support if it continues.')
            }
          })
        }
      },
      async initializeDevice () {
        try {
          const [token, iceServers] = await Promise.all([
            this.retrieveToken(),
            this.retrieveIceServers()
          ])

          this.$_device.setup(token, {
            rtcConfiguration: { iceServers },
            codecPreferences: ['opus', 'pcmu'],
            enableIceRestart: true,
            backoffMaxMs: 5000,
            fakeLocalDTMF: true,
            debug: true,
            closeProtection: true,
            Log: TwilioClientLogger
          })

          this.$_device.audio.disconnect(false)

          this.$_device.on('ready', this.onDeviceReady)
          this.$_device.on('connect', this.onDeviceConnect)
          this.$_device.on('disconnect', this.onDeviceDisconnect)
          this.$_device.on('offline', this.onDeviceOffline)
          this.$_device.on('error', this.onDeviceError)
        } catch (error) {
          throw new VError({
            name: 'TwilioDeviceSetupError',
            cause: error instanceof Error ? error : Error(error)
          }, 'Failed to setup Twilio device')
        }
      },
      async retrieveToken () {
        try {
          const response = await axios.get(`${jiminnyHost()}/api/v1/call-token/${this.team.id}`, {
            params: { type: 'softphone' }
          })
          return response.data.token
        } catch (error) {
          if (error.response) {
            Notifications.errorNotification({ message: error.response.data.error.message }).show()
          }

          throw new VError({
            name: 'RetrieveTokenError',
            cause: error instanceof Error ? error : Error(error)
          }, 'Failed to retrieve token')
        }
      },
      async retrieveIceServers () {
        try {
          const response = await axios.get(`${jiminnyHost()}/api/v1/call-token/${this.team.id}`, {
            params: { type: 'ice' }
          })

          return response.data
        } catch (error) {
          throw new VError({
            name: 'RetrieveIceServersError',
            cause: error instanceof Error ? error : Error(error)
          }, 'Failed to retrieve ICE servers')
        }
      },
      connect () {
        const connection = this.$_device.connect({
          activityId: this.activity.id,
          userId: this.user.id,
          userToken: this.user.hash,
          deviceId: this.deviceId,
          isGhost: this.isGhost
        })

        connection
          .on('warning', this.onConnectionWarning)
          .on('warning-cleared', this.onConnectionWarningCleared)
          .on('mute', this.onConnectionMute)
      },
      onDeviceReady () {
        this.$_logger.log('Device is ready')

        if (this.connectOnDeviceReady) {
          this.connect()
          this.connectOnDeviceReady = false
        }
      },
      onDeviceConnect (connection) {
        if (this.isGhost) {
          connection.mute(true)
        }
      },
      onDeviceDisconnect (connection) {
        this.$_logger.log('Connection is closed', connection)

        connection
          .removeListener('warning', this.onConnectionWarning)
          .removeListener('warning-cleared', this.onConnectionWarningCleared)
          .removeListener('mute', this.onConnectionMute)
      },
      onDeviceOffline () {
        this.$_logger.warn('Device is offline')

        if (!this.endedByUser) {
          this.refreshDeviceToken()
        }
      },
      onDeviceError (error) {
        this.$_logger.error(error)

        if (error.code === 31001) {
          Notifications.failedToPlaceCallNotification().show()
        } else if (error.code === 31003) {
          Notifications.offlineNotification().show()
        } else if (error.code === 31201) {
          swal({
            title: `Jiminny can't access your microphone`,
            type: 'error',
            html: `
              You might have accidentally blocked Jiminny, or another application might be using it. Please
              <a target="_blank" href="https://docs.jiminny.com/faqs/granting-jiminny-access-to-your-camera-or-microphone">follow our guide to resolve this</a>
              before you continue.
            `,
            confirmButtonText: 'Try again',
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then((result) => {
            if (result.value) {
              this.joinConferenceCall()
            }
          })
        } else if (error.code === 31208) {
          Notifications.microphoneBlockedNotification().show()
        }

        Sentry.withScope(scope => {
          scope.setExtra('debug', false)
          Sentry.captureMessage(error)
        })
      },
      onConnectionMute (isMuted) {
        this.setIsMuted({ isMuted, isGhost: this.isGhost })
      },
      onConnectionWarning (warning, data) {
        this.$_logger.warn(`${warning} raised`, data)

        switch (warning) {
          case 'high-rtt':
            highRttNotification = Notifications.highRttNotification().show()
            this.$_notificationsToCloseOnDestroy.push(highRttNotification)
            break
          case 'low-mos':
            lowMosNotification = Notifications.lowMosNotification().show()
            this.$_notificationsToCloseOnDestroy.push(lowMosNotification)
            break
          case 'high-jitter':
            highJitterNotification = Notifications.highJitterNotification().show()
            this.$_notificationsToCloseOnDestroy.push(highJitterNotification)
            break
          case 'high-packet-loss':
            highPacketLossNotification = Notifications.highPacketLossNotification().show()
            this.$_notificationsToCloseOnDestroy.push(highPacketLossNotification)
            break
          case 'constant-audio-input-level':
            if (!this.isMuted) {
              constantAudioInputLevelNotification = Notifications.constantAudioInputLevelNotification().show()
              this.$_notificationsToCloseOnDestroy.push(constantAudioInputLevelNotification)
            }
            break
          case 'constant-audio-output-level':
            constantAudioOutputLevelNotification = Notifications.constantAudioOutputLevelNotification().show()
            this.$_notificationsToCloseOnDestroy.push(constantAudioOutputLevelNotification)
            break
        }
      },
      onConnectionWarningCleared (warning) {
        this.$_logger.log(`${warning} cleared`)

        switch (warning) {
          case 'high-rtt':
            highRttNotification.close()
            break
          case 'low-mos':
            lowMosNotification.close()
            break
          case 'high-jitter':
            highJitterNotification.close()
            break
          case 'high-packet-loss':
            highPacketLossNotification.close()
            break
          case 'constant-audio-input-level':
            constantAudioInputLevelNotification.close()
            break
          case 'constant-audio-output-level':
            constantAudioOutputLevelNotification.close()
            break
        }
      },
      async refreshDeviceToken () {
        try {
          const token = await this.retrieveToken()
          this.$_device.setup(token)
        } catch (error) {
          this.$_logger.error(error)
        }
      },
      async leaveConferenceCall () {
        try {
          this.endedByUser = true
          await this.disconnectOnBackend()
          this.$_device.disconnectAll()
          this.exitCallScreen()
        } catch (error) {
          console.error(error)
        }
      },
      disconnectOnBackend () {
        axios.delete(`${jiminnyHost()}/api/v1/activity/softphone/${this.activity.id}`, {
          headers: {
            'X-Jiminny-Device-Id': this.deviceId
          }
        })
      },
      toggleMute () {
        const connection = this.$_device.activeConnection()

        if (connection) {
          const isMuted = connection.isMuted()
          connection.mute(!isMuted)
        }
      },
      handleDialpadKey (key) {
        if (this.$_device.activeConnection()) {
          this.$_device.activeConnection().sendDigits(key)
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// twilio-softphone.vue?2f03dd1e